import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HandHeart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { HELPER_NOTE_MAX } from "@/components/social/HelperNote";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Otter Assist V1 Phase 6 — the ONE reusable I Can Help control (OAD-074 / OAD-076).
// Used on the public Chatter post pill AND inside a Community Needs card, so there is a single
// responder surface. Eligibility is ALWAYS decided by the backend: the AI match count is never
// consulted and the client never guesses. Confirmation is required before an offer is created,
// and withdrawal is always permitted (a re-offer cooldown never blocks a withdrawal).

const REASON_COPY = {
  reoffer_cooldown_active: "You've changed your mind a couple of times — please try again shortly.",
  help_not_available: "This need is no longer open for help.",
  feature_unavailable: "This isn't available right now.",
  offer_already_active: "You've already offered to help.",
  helper_note_too_long: `Please keep your message to ${HELPER_NOTE_MAX} characters.`,
};

export function OtterAssistHelpButton({ assistId, initialState, testIdSuffix, size = "sm",
                                       onChanged, className = "" }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState("");
  const suffix = testIdSuffix || assistId;

  const { data } = useQuery({
    queryKey: ["oa-help-state", assistId],
    queryFn: async () => (await api.get(`/otter-assist/assists/${assistId}/help`)).data,
    enabled: !!assistId,
    initialData: initialState,
  });
  const offered = data?.my_response === "offered";

  // OAD-111 — a re-offer pre-fills the previous message; the helper may keep, edit or clear it.
  useEffect(() => { setNote(data?.helper_note || ""); }, [data?.helper_note, confirming]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["oa-help-state", assistId] });
    qc.invalidateQueries({ queryKey: ["oa-community-needs"] });
    qc.invalidateQueries({ queryKey: ["oa-my-needs"] });
    qc.invalidateQueries({ queryKey: ["oa-assistance-offered"] });
    qc.invalidateQueries({ queryKey: ["oa-need-detail"] });
    qc.invalidateQueries({ queryKey: ["oa-responders", assistId] });
    if (onChanged) onChanged();
  };

  const offer = useMutation({
    mutationFn: async () => (await api.post(`/otter-assist/assists/${assistId}/help`,
      { confirm: true, helper_note: note.trim() ? note.trim() : null })).data,
    onSuccess: () => { setConfirming(false); toast.success("They'll see that you may be able to help."); invalidate(); },
    onError: (e) => {
      setConfirming(false);
      const code = e?.response?.data?.detail;
      toast.error(REASON_COPY[code] || "That didn't work — please try again.");
    },
  });

  const withdraw = useMutation({
    mutationFn: async () => (await api.post(`/otter-assist/assists/${assistId}/help/withdraw`)).data,
    onSuccess: () => { toast.success("Your offer has been withdrawn."); invalidate(); },
    onError: () => toast.error("That didn't work — please try again."),
  });

  const busy = offer.isPending || withdraw.isPending;

  if (offered) {
    return (
      <span className={`inline-flex items-center gap-1.5 ${className}`}>
        <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-[11px] font-[640] text-teal-800"
          data-testid={`otter-assist-offered-${suffix}`}>
          <HandHeart className="h-3.5 w-3.5" /> Offered to Help
        </span>
        <Button type="button" size={size} variant="ghost" disabled={busy}
          className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground"
          onClick={() => withdraw.mutate()}
          data-testid={`otter-assist-withdraw-${suffix}`}>
          {withdraw.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Withdraw"}
        </Button>
      </span>
    );
  }

  // Rendered only when the backend says help can actually be offered right now — never as a
  // greyed-out control. The one exception is the reversal cooldown (OAD-076): after two quick
  // offer/withdraw flips we say WHEN they can offer again, so the control never just vanishes.
  if (!data?.can_help) {
    if (data?.reason === "reoffer_cooldown_active" && data?.cooldown_until) {
      return (
        <span className={`text-[11px] text-muted-foreground ${className}`}
          data-testid={`otter-assist-help-cooldown-${suffix}`}>
          You can offer again at {new Date(data.cooldown_until).toLocaleTimeString([], {
            hour: "numeric", minute: "2-digit" })}
        </span>
      );
    }
    return null;
  }

  return (
    <>
      <Button type="button" size={size} variant="outline" disabled={busy}
        className={`h-7 text-[11px] ${className}`}
        onClick={() => setConfirming(true)}
        data-testid={`otter-assist-help-${suffix}`}>
        <HandHeart className="mr-1 h-3.5 w-3.5" /> I Can Help
      </Button>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent data-testid={`otter-assist-help-confirm-${suffix}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Let this user know you may be able to help?</AlertDialogTitle>
            <AlertDialogDescription>
              They'll see your name and profile so they can reach out. You can withdraw at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <label className="text-[11px] font-[600] uppercase tracking-wide text-muted-foreground"
              htmlFor={`otter-assist-help-note-${suffix}`}>
              Add a message (optional)
            </label>
            <Textarea id={`otter-assist-help-note-${suffix}`} rows={3} value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional — tell them how you can help. Only they will see this."
              className="text-[13px]" data-testid={`otter-assist-help-note-${suffix}`} />
            <div className={`text-[11px] ${note.length > HELPER_NOTE_MAX
              ? "text-destructive" : "text-muted-foreground"}`}
              data-testid={`otter-assist-help-note-count-${suffix}`}>
              {note.length}/{HELPER_NOTE_MAX}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`otter-assist-help-cancel-${suffix}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={offer.isPending || note.length > HELPER_NOTE_MAX}
              onClick={(e) => { e.preventDefault(); offer.mutate(); }}
              data-testid={`otter-assist-help-submit-${suffix}`}>
              {offer.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Yes, I can help
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default OtterAssistHelpButton;
